/*
  Free gifts that come with a subscription's FIRST order.

  Order 1 is the theme's job and only the theme's: Skio's Surprise & Delight
  rules can only fire on order 2 and above, because order 1 is the checkout
  order. Later gifts are Skio's and nothing here touches them.

  The gift is the REAL sellable variant at its real price. A duplicate variant
  priced at zero would split that product's inventory into two pools, so what
  makes it free is an automatic product discount from a Shopify Function, not
  the price on the line. Cart Transform cannot do it: lineUpdate is rejected
  whenever a selling plan is present in the cart, and every cart here has one.

  See docs/superpowers/specs/2026-08-29-pdp-gift-schedule-design.md.
*/
(function (window) {
  'use strict';

  function tally(items) {
    var totals = {};
    (items || []).forEach(function (item) {
      var id = String(item.variantId);
      totals[id] = (totals[id] || 0) + (Number(item.quantity) || 0);
    });
    return totals;
  }

  /*
    What the cart should look like, against what it does. Returns the
    operations that close the gap — and, crucially, NOTHING when the cart is
    already right. The reconcile runs on every cart render, so a diff that
    reported spurious work would mutate the cart, trigger a render, and do it
    again forever.
  */
  function computeGiftDiff(expected, actual) {
    var wanted = tally(expected);
    var adds = [];
    var changes = [];
    var seen = {};

    (actual || []).forEach(function (line) {
      var id = String(line.variantId);
      if (seen[id]) {
        /* A second line for a gift already accounted for. Dropped rather than
           adjusted: the surviving line carries the whole quantity, and two
           lines of the same gift is how a customer ends up with two. */
        changes.push({ key: line.key, quantity: 0 });
        return;
      }
      seen[id] = true;
      var want = wanted[id] || 0;
      if (want !== (Number(line.quantity) || 0)) {
        changes.push({ key: line.key, quantity: want });
      }
    });

    /* Adds come out in the order the buy box lists the gifts — walking
       `expected`, not the tally's keys, because variant ids are numeric
       strings and JS iterates those in ascending numeric order, not
       insertion order. */
    var queued = {};
    (expected || []).forEach(function (item) {
      var id = String(item.variantId);
      if (seen[id] || queued[id] || !(wanted[id] > 0)) return;
      queued[id] = true;
      adds.push({ id: id, quantity: wanted[id] });
    });

    return { adds: adds, changes: changes };
  }

  /* The contract with the plan card's data-gift-items, written by the Liquid
     that read the awards: "variantId:qty,variantId:qty". */
  function parseGiftItems(planEl) {
    if (!planEl) return [];
    var raw = planEl.getAttribute('data-gift-items') || '';
    if (!raw) return [];
    return raw.split(',').reduce(function (acc, pair) {
      var parts = pair.split(':');
      var id = (parts[0] || '').trim();
      if (!id) return acc;
      acc.push({ variantId: id, quantity: Number(parts[1]) || 1 });
      return acc;
    }, []);
  }

  function addGiftsFor(planEl) {
    var items = parseGiftItems(planEl);
    if (!items.length) return Promise.resolve(null);

    var body = {
      items: items.map(function (item) {
        return {
          id: item.variantId,
          quantity: item.quantity,
          /* Same properties as every other gift write here: lines whose
             properties differ are kept apart by Shopify, and two lines of
             one gift is how a customer ends up charged for the second. */
          properties: { _free_gift: 'true', _gift_of: '' },
        };
      }),
    };

    var url = (window.routes && window.routes.cart_add_url) || '/cart/add';
    return fetch(url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        /* A gift that cannot be added must never surface as a failed
           add-to-cart: the subscription is already in the cart and the sale is
           made. The reconcile on the next render will try again. */
        return null;
      });
  }

  var reconcileInFlight = false;

  function readGiftState() {
    var el = document.querySelector('[data-pdp-gift-state]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (error) {
      return null;
    }
  }

  /*
    Covers what the add path cannot: the subscription removed, the plan changed
    and re-added, a second subscription product, quantity edits, and edits made
    on /cart rather than in the drawer.

    Every operation here mutates the cart, which re-renders the drawer, which
    calls this again. The in-flight guard and computeGiftDiff returning nothing
    for a correct cart are the two things that terminate it — if you ever see
    repeated /cart/change calls, the diff is reporting work on a correct cart
    and that is where the fix belongs, not in a longer timeout.
  */
  /* What the cart holds RIGHT NOW, from /cart.js — never from the rendered
     state block. The block is a snapshot of the cart at render time; between
     an add and the re-render that follows it, it still says the gift is
     missing. On 2026-09-08 a second reconcile ran in that window (triggered by
     the first one's own "changed" event), read the stale block, added every
     gift again, and Shopify merged the pairs into quantity 2. The discount
     then freed one unit of each and split the lines, so checkout showed each
     gift twice: once free, once charged. Reading the cart itself closes that
     window: a cart that already has the gift never gets it again. */
  function liveGiftLines() {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    /* no-store: /cart.js carries no browser cache headers, and on a
       back-navigation from checkout the browser is happy to hand back the
       copy it fetched before the gifts went in. A stale "no gifts" answer
       here is a second add, and a second add is a charged duplicate. */
    return fetch(/\.js(\?|$)/.test(cartUrl) ? cartUrl : cartUrl + '.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (!cart || !cart.items) return null;
        return cart.items
          .filter(function (item) {
            return item.properties && item.properties._free_gift === 'true';
          })
          .map(function (item) {
            return { variantId: item.variant_id, quantity: item.quantity, key: item.key };
          });
      })
      .catch(function () {
        return null;
      });
  }

  function reconcileGifts() {
    /* Resolves true only when the cart was actually changed. The in-flight
       guard stays up until the drawer has been re-rendered from the new cart,
       so no trigger can run against the old state block in between. */
    if (reconcileInFlight || prepareInFlight) return Promise.resolve(false);
    var state = readGiftState();
    if (!state) return Promise.resolve(false);

    reconcileInFlight = true;
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    var json = { 'Content-Type': 'application/json', Accept: 'application/json' };

    return liveGiftLines()
      .then(function (actual) {
        /* No live cart, no write: guessing from the snapshot is how the
           duplicates happened. The next render will try again. */
        if (!actual) return false;
        /* Belt and braces: a gift the rendered snapshot already lists is
           never added, whatever the live read says. Two sources have to
           agree the gift is missing before it goes in; either alone can
           be stale, and the failure mode of "stale" is a paid duplicate. */
        var snapshotHas = tally(state.actual || []);
        var ops = computeGiftDiff(state.expected || [], actual);
        ops.adds = ops.adds.filter(function (add) {
          return !snapshotHas[String(add.id)];
        });
        if (!ops.adds.length && !ops.changes.length) return false;
        return runGiftOps(ops, addUrl, json).then(function () {
          return refreshCartDrawer().then(function () {
            return true;
          });
        });
      })
      .catch(function () {
        return false;
      })
      .finally(function () {
        reconcileInFlight = false;
      });
  }

  function runGiftOps(ops, addUrl, json) {
    /* Every quantity change in ONE /cart/update, keyed by line key. One
       request is one cart write and one drawer render; a chain of
       /cart/change calls was a render per gift, each showing the survivors
       at full price for a beat. */
    var work = Promise.resolve();
    if (ops.changes.length) {
      var updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update';
      var updates = {};
      ops.changes.forEach(function (change) {
        updates[change.key] = change.quantity;
      });
      work = fetch(updateUrl + '.js', {
        method: 'POST',
        headers: json,
        body: JSON.stringify({ updates: updates }),
      });
    }

    if (ops.adds.length) {
      work = work.then(function () {
        return postGiftAdds(ops.adds, addUrl, json);
      });
    }

    return work;
  }

  function postGiftAdds(adds, addUrl, json) {
    return fetch(addUrl + '.js', {
      method: 'POST',
      headers: json,
      body: JSON.stringify({
        /* Shopify puts each added line at the TOP of the cart, so a batch
           lands reversed. Posting the gifts last-to-first leaves the cart
           reading top-to-bottom in buy-box order (glass jar first,
           blueprint last). */
        items: adds.slice().reverse().map(function (add) {
          return {
            id: add.id,
            quantity: add.quantity,
            properties: { _free_gift: 'true', _gift_of: '' },
          };
        }),
      }),
    });
  }

  /*
    Gifts go into the cart BEFORE the subscription, so the subscription is the
    last line added and therefore the first line at checkout. Shopify inserts
    each new line at the top and offers no way to reorder; when the reconcile
    added the gifts after the subscription had landed, checkout led with four
    free gifts and showed the product the customer actually bought last
    (2026-09-09).

    Reads the live cart first so a gift already present is never added twice,
    and holds prepareInFlight so no reconcile can run against the half-built
    cart in between — with no subscription line yet, a reconcile would count
    these gifts as unearned and take them straight back out.
  */
  var prepareInFlight = false;

  function addGiftsAhead(planEl) {
    var items = parseGiftItems(planEl);
    if (!items.length) return Promise.resolve(false);

    prepareInFlight = true;
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    var json = { 'Content-Type': 'application/json', Accept: 'application/json' };

    return liveGiftLines()
      .then(function (actual) {
        /* No live cart, no write: the reconcile adds them after the
           subscription lands, at the cost of the order. */
        if (!actual) return false;
        var adds = computeGiftDiff(items, actual).adds;
        if (!adds.length) return false;
        return postGiftAdds(adds, addUrl, json).then(function () {
          return true;
        });
      })
      .catch(function () {
        /* Same stance as addGiftsFor: a gift that cannot go in must never
           block the sale. The subscription still gets added. */
        return false;
      })
      .finally(function () {
        prepareInFlight = false;
      });
  }

  function selectedPlanFor(form) {
    var buybox = form.closest('[data-master-buybox]');
    if (!buybox) return null;
    return (
      buybox.querySelector('[data-master-plan].is-selected') ||
      buybox.querySelector('[data-master-plan][aria-checked="true"]')
    );
  }

  window.PDPGifts = window.PDPGifts || {};
  window.PDPGifts.computeGiftDiff = computeGiftDiff;
  window.PDPGifts.parseGiftItems = parseGiftItems;
  window.PDPGifts.addGiftsFor = addGiftsFor;
  window.PDPGifts.reconcileGifts = reconcileGifts;
  window.PDPGifts.addGiftsAhead = addGiftsAhead;

  /* The buy box posts a native {% form 'product' %} through sht-prd-frm. That
     form is left alone and the gifts are posted separately, rather than being
     bundled into it: /cart/add.js is atomic across items[], so one
     out-of-stock gift would otherwise lose the whole sale.

     Order of writes: gifts first, subscription second, so the subscription is
     the top line at checkout (see addGiftsAhead). The first submit is
     intercepted here — before sht-prd-frm's own listener, which sits on the
     form element itself and so runs after a capturing listener on the
     document — the gifts are posted, and the form is submitted again carrying
     a flag that lets the second pass straight through to the theme.

     Only ONE path may add a gift. A second add here, racing the reconcile
     that the drawer render fires, once wrote every gift twice (checkout showed
     "2 × Deep Sleep Earplugs", 2026-09-08). Both paths now read the live cart
     before writing and each holds a guard the other honours, so whichever
     runs first is the only one that writes. The reconcile's remaining job on
     an add is the case this path cannot cover: a gift that failed to go in. */
  document.addEventListener(
    'submit',
    function (event) {
      var form =
        event.target && event.target.closest
          ? event.target.closest('[data-type="add-to-cart-form"]')
          : null;
      if (!form) return;
      if (!form.closest('[data-master-buybox]')) return;

      if (form.dataset.pdpGiftsAhead === 'true') {
        delete form.dataset.pdpGiftsAhead;
        window.setTimeout(reconcileGifts, 600);
        return;
      }

      /* A second tap while the gifts are still going in is dropped: letting
         it through would read a cart the first batch has not reached yet and
         post the gifts again. */
      if (prepareInFlight) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      var plan = selectedPlanFor(form);
      if (!parseGiftItems(plan).length) {
        window.setTimeout(reconcileGifts, 600);
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      addGiftsAhead(plan).then(function () {
        form.dataset.pdpGiftsAhead = 'true';
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });
    },
    true
  );

  /*
    Re-render the drawer after we have changed the cart, or it keeps showing
    gifts we just removed. Mirrors what the theme does after its own cart
    writes: pull the cart and the drawer's sections, then hand them over.
  */
  function refreshCartDrawer() {
    var form = document.querySelector('sht-cart-drwr-frm');
    if (!form || typeof form.renderContents !== 'function') return Promise.resolve(null);

    var ids = ((form.getSectionsToRender && form.getSectionsToRender()) || [])
      .map(function (item) {
        return item.section || item.id;
      })
      .filter(Boolean);
    if (!ids.length) return Promise.resolve(null);
    /* The header badge counts gift lines too, so it moves whenever the
       reconcile does; the drawer's own list does not include it. */
    if (ids.indexOf('header-cart-status') === -1) ids.push('header-cart-status');

    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return Promise.all([
      fetch(/\.js(\?|$)/.test(cartUrl) ? cartUrl : cartUrl + '.js').then(function (r) {
        return r.ok ? r.json() : null;
      }),
      fetch('?sections=' + ids.join(','), { cache: 'no-store' }).then(function (r) {
        return r.ok ? r.json() : null;
      }),
    ])
      .then(function (results) {
        var cart = results[0];
        var sections = results[1];
        if (!cart || !sections) return null;
        cart.sections = sections;
        form.renderContents(cart);
        if (sections['header-cart-status'] && window.SHTUpdateHeaderCartStatus) {
          window.SHTUpdateHeaderCartStatus(sections['header-cart-status']);
        }
        return cart;
      })
      .catch(function () {
        return null;
      });
  }

  /*
    The reconcile has to follow the CART, and the cart changes in places this
    script never hears about — the customer removing the subscription in the
    drawer, an upsell being added, a quantity stepper. All of those re-render
    the drawer, replacing the gift-state block, so watching the drawer for
    re-renders is what catches them.

    Debounced, because a single render fires many mutations. Safe to re-enter
    because computeGiftDiff returns nothing for a correct cart, so a reconcile
    that changes nothing does not trigger another render.
  */
  function watchCartRenders() {
    var host = document.querySelector('sht-cart-drwr') || document.body;
    var timer = null;
    new MutationObserver(function () {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(reconcileGifts, 200);
    }).observe(host, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', function () {
    reconcileGifts();
    watchCartRenders();
  });

  window.PDPGifts.refreshCartDrawer = refreshCartDrawer;
})(window);
